import { useState } from "react";
import {
  WEBSITE_PAID_ENTITLEMENTS,
  websiteEntitlementsIncluded,
} from "../../shared/plan/websiteEntitlements";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiError } from "../../shared/api/client";
import { vnd } from "../../shared/api/reports";
import { planComparisonCodes } from "@/shared/plan/planComparisonCodes";
import {
  featureLabel,
  statusLabel,
  subscriptionApi,
  subscriptionQueryKeys,
  type CurrentSubscription,
  type PlanQuote,
  type PlanSummary,
} from "../../shared/api/subscription";
import { saasBillingApi, saasBillingQueryKeys } from "../../shared/api/saasBilling";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UpgradePlanCard } from "./components/UpgradePlanCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Gói thuê bao — phòng khám tự mua.
 *
 * Phòng khám bắt đầu bằng 1 tháng dùng thử đầy đủ tính năng. Gói nào đang bán và mỗi gói gồm
 * những màn hình nào là do quản trị viên nền tảng cấu hình (ADR-0053), nên trang này không biết tên
 * gói nào: nó vẽ đúng những gì máy chủ gửi về.
 *
 * Gói niêm yết 0đ được **chọn** (@POST /free-plan@) — không qua ví, và là đường ra khỏi chế độ chỉ
 * xem mà không cần nạp tiền. Gói tính phí được mua bằng một trong hai đường:
 *
 * - **Trừ ví** (@POST /purchase@) — tức thì, nếu ví đủ tiền.
 * - **Trả thẳng** (@POST /pay@) — mở PayOS, không cần nạp ví trước. Gói kích hoạt khi webhook báo
 *   tiền đã về, KHÔNG phải khi phòng khám bấm nút.
 *
 * Trước #18990 nút này gọi @/subscribe@ — đường THỬ kích hoạt miễn phí, mặc định TẮT ở backend.
 * Nghĩa là trên cấu hình thật, phòng khám bấm "Đăng ký" chỉ nhận về 404.
 *
 * Giá lấy từ @/quotes@ chứ không phải @monthlyPriceVnd@: phòng khám có giá thoả thuận riêng phải
 * nhìn thấy đúng con số sắp bị thu.
 */
export function SubscriptionSettingsPage() {
  const qc = useQueryClient();
  const summary = useQuery({
    queryKey: subscriptionQueryKeys.summary(),
    queryFn: () => subscriptionApi.summary(),
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const quotes = useQuery({
    queryKey: subscriptionQueryKeys.quotes(),
    queryFn: () => subscriptionApi.quotes(),
  });
  const billing = useQuery({
    queryKey: saasBillingQueryKeys.summary(),
    queryFn: () => saasBillingApi.summary(),
  });

  /** Mua bằng ví — tức thì khi ví đủ. */
  const purchase = useMutation({
    mutationFn: (planCode: string) => subscriptionApi.purchase(planCode),
    onSuccess: (bought) => {
      setError(null);
      setNotice(
        `Đã kích hoạt ${planName(bought.planCode)} tới ${new Date(
          bought.endsAt
        ).toLocaleDateString("vi-VN")}. Ví còn ${vnd(bought.walletBalanceVnd)}.`
      );
      void qc.invalidateQueries({ queryKey: subscriptionQueryKeys.summary() });
      void qc.invalidateQueries({ queryKey: saasBillingQueryKeys.summary() });
    },
    onError: (e: unknown) => {
      setNotice(null);
      // 402 đã kèm sẵn số còn thiếu — hiện nguyên văn còn hơn viết lại thành "không đủ tiền",
      // vì phòng khám cần biết thiếu BAO NHIÊU để nạp, nếu không họ sẽ nạp thiếu.
      setError(apiError(e).detail);
    },
  });

  /** Chọn gói miễn phí — không qua ví, và là đường ra khỏi chỉ-đọc mà không cần nạp tiền. */
  const selectFree = useMutation({
    mutationFn: (planCode: string) => subscriptionApi.selectFreePlan(planCode),
    onSuccess: (chosen) => {
      setError(null);
      setNotice(`Đã chuyển sang gói ${planName(chosen.planCode)}.`);
      // Gói đổi là tập màn hình và trạng thái thanh toán đổi theo: menu, bộ chặn đường dẫn và
      // banner chỉ-đọc đều phải đọc lại, nếu không phòng khám vừa thoát chỉ-đọc vẫn thấy mình bị khoá.
      void qc.invalidateQueries();
    },
    onError: (e: unknown) => {
      setNotice(null);
      setError(apiError(e).detail);
    },
  });

  /** Trả thẳng qua PayOS — không cần nạp ví trước. */
  const payDirect = useMutation({
    mutationFn: (planCode: string) => subscriptionApi.pay(planCode),
    onSuccess: (intent) => {
      setError(null);
      if (intent.checkoutUrl) {
        window.open(intent.checkoutUrl, "_blank", "noopener");
        setNotice(
          "Đã mở trang thanh toán PayOS. Gói sẽ tự kích hoạt sau khi tiền về — bạn không cần " +
            "quay lại trang này."
        );
      } else {
        setError("Chưa tạo được link thanh toán. Vui lòng thử lại.");
      }
    },
    onError: (e: unknown) => {
      setNotice(null);
      setError(apiError(e).detail);
    },
  });

  const current = summary.data?.current ?? null;
  const plans = summary.data?.plans ?? [];
  /** Tên gói do quản trị viên đặt; mã chỉ là phương án cuối khi gói không có trong bảng. */
  const planName = (code: string) => plans.find((p) => p.code === code)?.name ?? code;
  const walletVnd = billing.data?.balanceVnd ?? 0;
  const busyCode =
    (purchase.isPending ? purchase.variables : null) ??
    (payDirect.isPending ? payDirect.variables : null) ??
    (selectFree.isPending ? selectFree.variables : null) ??
    null;

  /**
   * Gói để nâng lên: gói bán được RẺ NHẤT trong số đắt hơn gói hiện tại.
   *
   * Không đề xuất gói đắt nhất — nâng một bậc là bước người dùng hay muốn, và đề xuất bậc cao nhất
   * đọc như bán hàng chứ không như giúp đỡ.
   */
  const currentPrice =
    (quotes.data ?? []).find((q) => q.planCode === current?.planCode)?.priceVnd ?? null;
  const upgradeTarget =
    currentPrice == null
      ? null
      : ((quotes.data ?? [])
          .filter((q) => q.priceVnd > currentPrice)
          .sort((a, b) => a.priceVnd - b.priceVnd)[0]?.planCode ?? null);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Gói thuê bao"
        description={
          <>
            Phòng khám mới được dùng thử 1 tháng đầy đủ tính năng. Sau đó chọn một gói miễn phí,
            hoặc mua gói tính phí bằng <strong>số dư ví</strong> hoặc{" "}
            <strong>trả thẳng qua PayOS</strong>.
          </>
        }
      />

      {summary.isLoading && (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      )}
      {summary.isError && (
        <p className="text-sm text-destructive">
          Không tải được thông tin gói thuê bao.
        </p>
      )}

      {!summary.isLoading && (
        <>
          <CurrentPlanCard current={current} quotes={quotes.data ?? []} />

          {/*
            Nâng gói giữa kỳ (#18990). Chỉ hiện khi phòng khám đang ở một gói RẺ HƠN một gói bán
            được — nâng gói khác mua gói: nó có hiệu lực ngay và chỉ thu phần chênh cho thời gian
            còn lại, nên không gộp vào bảng "Chọn gói" phía dưới.
          */}
          {upgradeTarget && <UpgradePlanCard targetPlanCode={upgradeTarget} />}

          <Card>
            <CardHeader>
              <CardTitle>Chọn gói</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <PlanComparison
                plans={plans}
                currentCode={current?.planCode ?? null}
                quotes={quotes.data ?? []}
                walletVnd={walletVnd}
                onPurchase={(code) => purchase.mutate(code)}
                onPayDirect={(code) => payDirect.mutate(code)}
                onSelectFree={(code) => selectFree.mutate(code)}
                busyCode={busyCode}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              {notice && <p className="text-sm text-emerald-700">{notice}</p>}
              {billing.data && !billing.data.reconciled && (
                <p className="text-sm text-amber-700">
                  Số dư ví đang lệch so với sổ giao dịch, nên con số phía trên chưa chắc đúng.
                  Addy đã ghi nhận và sẽ đối soát — bạn vẫn trả thẳng được bình thường.
                </p>
              )}
            </CardContent>
          </Card>

          <PaymentNote walletVnd={walletVnd} />
        </>
      )}
    </div>
  );
}

// =============================================================== Current plan

function trialDaysRemaining(activeTo: string | null): number | null {
  if (!activeTo) return null;
  const ms = new Date(activeTo).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function CurrentPlanPrice({
  listPriceVnd,
  quote,
}: {
  listPriceVnd: number;
  quote: PlanQuote | null;
}) {
  if (listPriceVnd <= 0 && !quote) {
    return <strong className="text-base">Miễn phí</strong>;
  }
  const price = quote?.priceVnd ?? listPriceVnd;
  return (
    <>
      <strong className="text-base">{vnd(price)}</strong>
      {quote?.contracted && (
        <div className="text-[11px] text-emerald-700">
          Giá riêng theo hợp đồng (niêm yết {vnd(quote.listPriceVnd)})
        </div>
      )}
    </>
  );
}

function CurrentPlanCard({
  current,
  quotes,
}: {
  current: CurrentSubscription | null;
  quotes: PlanQuote[];
}) {
  if (!current) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Gói hiện tại</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Chưa có gói đang hoạt động — vui lòng đăng ký gói Cơ bản hoặc Chuyên
            nghiệp bên dưới để tiếp tục sử dụng đầy đủ tính năng.
          </p>
        </CardContent>
      </Card>
    );
  }

  const isTrial = current.status === "TRIALING";
  const daysLeft = isTrial ? trialDaysRemaining(current.activeTo) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gói hiện tại</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isTrial && daysLeft != null && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Bạn đang dùng thử — còn <strong>{daysLeft} ngày</strong>. Đăng ký gói
            trả phí để không bị gián đoạn khi hết hạn.
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">Tên gói</div>
            <strong className="text-base">{current.planName}</strong>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Phí hàng tháng</div>
            {/* Giá của phòng khám NÀY, không phải bảng giá. Thẻ này và bảng chọn gói phía dưới
                đọc cùng một nguồn — hai con số khác nhau trên cùng màn hình thì con số nào cũng
                không còn đáng tin. */}
            <CurrentPlanPrice
              listPriceVnd={current.monthlyPriceVnd}
              quote={quotes.find((q) => q.planCode === current.planCode) ?? null}
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Trạng thái</div>
            <Badge variant={current.status === "ACTIVE" ? "success" : "warning"}>
              {statusLabel(current.status)}
            </Badge>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Hiệu lực từ</div>
            <strong>
              {new Date(current.activeFrom).toLocaleDateString("vi-VN")}
            </strong>
            {current.activeTo && (
              <div className="text-xs text-muted-foreground">
                đến {new Date(current.activeTo).toLocaleDateString("vi-VN")}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================== Plan compare

function PlanComparison({
  plans,
  currentCode,
  quotes,
  walletVnd,
  onPurchase,
  onPayDirect,
  onSelectFree,
  busyCode,
}: {
  plans: PlanSummary[];
  currentCode: string | null;
  quotes: PlanQuote[];
  walletVnd: number;
  onPurchase: (code: string) => void;
  onPayDirect: (code: string) => void;
  onSelectFree: (code: string) => void;
  busyCode: string | null;
}) {
  if (plans.length === 0) {
    return <p className="text-sm text-muted-foreground">Chưa có gói nào.</p>;
  }

  /*
   * #19892 mục 3 + 4 — hai mã bị ẩn khỏi bảng này: `module.bhyt-export` và
   * `module.advanced-reports`. Cả hai được seed 'on' cho PRO/TRIAL nên bảng đang hiện "✓" cho
   * tính năng KHÔNG TỒN TẠI trong mã. Luật + lý do ở `shared/plan/planComparisonCodes.ts`; mã
   * vẫn được giữ nguyên ở mọi chỗ khác, và không cổng backend nào bị thêm/bớt cho chúng.
   */
  const allCodes = planComparisonCodes(
    Array.from(
      new Set(plans.flatMap((p) => p.features.map((f) => f.code)))
    ).sort()
  );

  const valueFor = (plan: PlanSummary, code: string): string => {
    const f = plan.features.find((x) => x.code === code);
    if (!f) return "—";
    if (f.valueText) {
      if (f.valueText === "on") return "✓";
      if (f.valueText === "off") return "—";
      return f.valueText;
    }
    if (f.valueInt != null) return f.valueInt.toLocaleString("vi-VN");
    return "—";
  };

  // Màn hình theo nhóm menu (ADR-0053), theo thứ tự máy chủ gửi về. Một nhóm xuất hiện khi ít
  // nhất một gói có màn hình trong nhóm đó.
  const screenGroups = Array.from(new Set(plans.flatMap((p) => p.screens.map((g) => g.group))));
  const screensOf = (plan: PlanSummary, group: string): string[] =>
    plan.screens.find((g) => g.group === group)?.screens ?? [];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tính năng / hạn mức</TableHead>
          {plans.map((p) => (
            <TableHead
              key={p.id}
              className={
                p.code === currentCode
                  ? "text-center align-top bg-primary/10"
                  : "text-center align-top"
              }
            >
              <div className="text-sm font-semibold normal-case tracking-normal text-foreground">
                {p.name}
              </div>
              <PlanPrice
                listPriceVnd={p.monthlyPriceVnd}
                quote={quotes.find((q) => q.planCode === p.code) ?? null}
              />
              <div className="mt-1.5 flex flex-col items-center gap-1">
                <PlanAction
                  code={p.code}
                  isCurrent={p.code === currentCode}
                  quote={quotes.find((q) => q.planCode === p.code) ?? null}
                  walletVnd={walletVnd}
                  onPurchase={onPurchase}
                  onPayDirect={onPayDirect}
                  onSelectFree={onSelectFree}
                  busyCode={busyCode}
                />
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {screenGroups.map((group) => (
          <TableRow key={`screens-${group}`}>
            <TableCell className="align-top">{group}</TableCell>
            {plans.map((p) => (
              <TableCell key={p.id} className="text-center align-top text-xs">
                {screensOf(p, group).join(" · ") || "—"}
              </TableCell>
            ))}
          </TableRow>
        ))}
        {allCodes.map((code) => (
          <TableRow key={code}>
            <TableCell>{featureLabel(code)}</TableCell>
            {plans.map((p) => (
              <TableCell key={p.id} className="text-center">
                {valueFor(p, code)}
              </TableCell>
            ))}
          </TableRow>
        ))}
        {/*
          Hai quyền của kênh website mà bảng này chưa từng nói tới (#19596). Suy từ giá NIÊM YẾT
          theo đúng luật `isPaidPlan` của backend, nên nó không thể lệch với thứ Addy Sites thực sự
          chặn — xem `shared/plan/websiteEntitlements.ts`.
        */}
        {WEBSITE_PAID_ENTITLEMENTS.map((row) => (
          <TableRow key={row.label}>
            <TableCell>{row.label}</TableCell>
            {plans.map((p) => (
              <TableCell key={p.id} className="text-center">
                {websiteEntitlementsIncluded(p.monthlyPriceVnd) ? "Có" : "—"}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Giá của gói ĐỐI VỚI phòng khám này — nói rõ khi nó khác bảng giá. */
function PlanPrice({
  listPriceVnd,
  quote,
}: {
  listPriceVnd: number;
  quote: PlanQuote | null;
}) {
  if (listPriceVnd <= 0 && !quote) {
    return (
      <div className="text-[11px] font-normal normal-case tracking-normal text-muted-foreground">
        Miễn phí
      </div>
    );
  }
  const price = quote?.priceVnd ?? listPriceVnd;
  return (
    <div className="text-[11px] font-normal normal-case tracking-normal text-muted-foreground">
      {quote?.contracted && (
        <span className="mr-1 line-through opacity-60">{vnd(quote.listPriceVnd)}</span>
      )}
      <span className={quote?.contracted ? "font-semibold text-foreground" : undefined}>
        {vnd(price)}
      </span>{" "}
      / tháng
      {quote?.contracted && (
        <div className="text-[10px] text-emerald-700">Giá riêng theo hợp đồng</div>
      )}
    </div>
  );
}

/**
 * Hai đường mua, hiện cùng lúc.
 *
 * <p>Nút chính đổi theo số dư ví: ví đủ thì mua bằng ví là một cú bấm, ví thiếu thì đẩy trả thẳng
 * lên trước. Nút ví khi thiếu tiền KHÔNG bị ẩn mà bị tắt kèm số còn thiếu — ẩn đi thì phòng khám
 * không biết mình thiếu bao nhiêu để nạp, và sẽ nạp thiếu.
 *
 * <p>TRIAL không có nút: nó tự cấp lúc đăng ký. Gói miễn phí có đúng một nút "Chọn gói này".
 */
function PlanAction({
  code,
  isCurrent,
  quote,
  walletVnd,
  onPurchase,
  onPayDirect,
  onSelectFree,
  busyCode,
}: {
  code: string;
  isCurrent: boolean;
  quote: PlanQuote | null;
  walletVnd: number;
  onPurchase: (code: string) => void;
  onPayDirect: (code: string) => void;
  onSelectFree: (code: string) => void;
  busyCode: string | null;
}) {
  if (isCurrent) {
    return <Badge variant="success">Đang dùng</Badge>;
  }
  if (code === "TRIAL") {
    return (
      <span className="text-[11px] font-normal normal-case tracking-normal text-muted-foreground">
        Tự động khi đăng ký
      </span>
    );
  }
  if (!quote) {
    return (
      <span className="text-[11px] font-normal normal-case tracking-normal text-muted-foreground">
        Đang tải giá…
      </span>
    );
  }

  const busy = busyCode === code;
  const anyBusy = busyCode != null;

  // Gói niêm yết 0đ được CHỌN, không được mua: không có gì để trừ ví hay trả thẳng. (Giá hợp đồng
  // 0đ của một gói tính phí là chuyện khác, và máy chủ không coi đó là gói miễn phí.)
  if (quote.listPriceVnd === 0) {
    return (
      <Button
        type="button"
        size="sm"
        onClick={() => onSelectFree(code)}
        disabled={anyBusy}
        className="w-full"
      >
        {busy ? "Đang xử lý…" : "Chọn gói này"}
      </Button>
    );
  }

  const enough = walletVnd >= quote.priceVnd;
  const missing = quote.priceVnd - walletVnd;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={enough ? "default" : "outline"}
        onClick={() => onPurchase(code)}
        disabled={anyBusy || !enough}
        title={enough ? undefined : `Ví thiếu ${vnd(missing)}`}
        className="w-full"
      >
        {busy ? "Đang xử lý…" : "Dùng số dư ví"}
      </Button>
      {!enough && (
        <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
          Ví thiếu {vnd(missing)}
        </span>
      )}
      <Button
        type="button"
        size="sm"
        variant={enough ? "outline" : "default"}
        onClick={() => onPayDirect(code)}
        disabled={anyBusy}
        className="w-full"
      >
        {busy ? "Đang xử lý…" : "Trả thẳng"}
      </Button>
    </>
  );
}

// =============================================================== Payment note

function PaymentNote({ walletVnd }: { walletVnd: number }) {
  return (
    <Card className="bg-muted/40">
      <CardHeader>
        <CardTitle>Thanh toán</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm text-muted-foreground">
        <p>
          Số dư ví: <strong className="text-foreground">{vnd(walletVnd)}</strong>. Nạp thêm ở{" "}
          <Link to="/settings/billing" className="underline underline-offset-2">
            Ví &amp; thanh toán
          </Link>
          .
        </p>
        <p>
          <strong>Trả thẳng</strong> mở trang PayOS trong tab mới. Bạn không cần quay lại trang
          này: gói tự kích hoạt khi tiền về. Nếu đóng trình duyệt giữa chừng, đơn vẫn còn và tiền
          về lúc nào thì gói chạy lúc đó.
        </p>
      </CardContent>
    </Card>
  );
}
